import React from 'react';
import { Calendar, Clock, Star, CheckCircle, XCircle, ArrowRight, AlertCircle, Zap, Brain, FileText, Globe, TrendingUp, Code } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SEOHead } from '@/components/SEOHead';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';

export const TestGeminiPage = () => {
  const { language } = useLanguage();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": language === 'fr' ? "Nous avons testé Gemini 2.5 pour vous" : "We Have Tested Gemini 2.5 for You",
    "description": language === 'fr' ? "Test complet de Google Gemini 2.5 Pro pour les professionnels du droit" : "Complete test of Google Gemini 2.5 Pro for legal professionals",
    "author": { "@type": "Organization", "name": "Gaius" },
    "publisher": { "@type": "Organization", "name": "Gaius", "logo": { "@type": "ImageObject", "url": "https://gaius-tech.com/logo.png" } },
    "datePublished": "2026-01-20",
    "dateModified": "2026-01-20"
  };

  if (language === 'fr') {
    return (
      <div className="min-h-screen bg-background">
        <SEOHead 
          title="Test Gemini 2.5 pour avocats : avis complet" 
          description="Test de Google Gemini 2.5 Pro pour le droit. Fenêtre de contexte 1M tokens, multimodalité, performances juridiques." 
          keywords="gemini juridique, google gemini avocat, ia générative avocats, gemini 2.5 pro, analyse documents ia, llm juridique" 
          canonicalUrl="https://gaius-tech.com/articles/test-gemini" 
          alternateUrlFr="https://gaius-tech.com/articles/test-gemini"
          alternateUrlEn="https://gaius-tech.com/articles/test-gemini"
          pageType="article"
          structuredData={structuredData}
        />
        
        {/* Header */}
        <div className="bg-white border-b">
          <div className="max-w-4xl mx-auto px-4 py-12 text-center">
            <Badge variant="secondary" className="mb-4 bg-blue-100 text-blue-800">AI News</Badge>
            <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
              Nous avons testé Gemini 2.5 pour vous
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Le nouveau modèle de Google à l'épreuve du droit : 1 million de tokens de contexte, multimodalité native et raisonnement avancé.
            </p>
            <div className="flex items-center justify-center space-x-6 text-sm text-muted-foreground">
              <span className="flex items-center space-x-1">
                <Calendar className="w-4 h-4" />
                <span>20 janvier 2026</span>
              </span>
              <span className="flex items-center space-x-1">
                <Clock className="w-4 h-4" />
                <span>14 min de lecture</span>
              </span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="prose prose-lg max-w-none">
            
            {/* Introduction */}
            <p className="text-xl leading-relaxed mb-8">
              <strong>Google Gemini 2.5 Pro</strong> représente l'aboutissement de plusieurs années de recherche chez Google DeepMind. Avec sa fenêtre de contexte d'<strong>un million de tokens</strong> et ses capacités multimodales natives, il promet de révolutionner l'analyse documentaire. Nous l'avons testé intensivement dans un contexte juridique exigeant.
            </p>

            <p className="mb-8">
              Question centrale de notre test : <strong>Gemini 2.5 peut-il devenir l'assistant polyvalent des juristes ?</strong> Voici notre analyse détaillée.
            </p>

            {/* Key Stats */}
            <div className="bg-primary/10 border-l-4 border-primary p-6 my-8">
              <div className="flex items-start">
                <TrendingUp className="w-6 h-6 text-primary mt-1 mr-4 flex-shrink-0" />
                <div>
                  <h4 className="text-primary font-semibold text-lg mb-2">Spécifications techniques</h4>
                  <ul className="text-foreground/80 space-y-1 mb-0">
                    <li>• <strong>1 million de tokens</strong> de fenêtre de contexte (≈ 750 000 mots)</li>
                    <li>• <strong>Multimodal natif</strong> : texte, images, PDF, audio, vidéo</li>
                    <li>• <strong>Latence moyenne</strong> : 2-4 secondes pour des requêtes complexes</li>
                    <li>• <strong>Date de coupure</strong> : données jusqu'à fin 2025</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Section 1 */}
            <h2 className="text-3xl font-bold mt-12 mb-6">La révolution du contexte : analyser des centaines de pages</h2>

            <p className="mb-6">
              La caractéristique la plus impressionnante de <strong>Gemini 2.5 Pro</strong> est sa capacité à ingérer et analyser des documents volumineux en une seule fois. Là où GPT-4 se limite à environ 128 000 tokens, Gemini en accepte <strong>huit fois plus</strong>.
            </p>

            <p className="mb-6">
              En pratique, cela signifie pouvoir charger :
            </p>

            <ul className="list-disc pl-6 space-y-3 mb-8">
              <li><strong>Un contrat de fusion-acquisition complet</strong> avec tous ses annexes</li>
              <li><strong>Un dossier de contentieux</strong> incluant conclusions, pièces et jurisprudences</li>
              <li><strong>Plusieurs centaines de pages de due diligence</strong> en une seule session</li>
              <li><strong>Un code entier</strong> pour analyse réglementaire (Code de commerce, Code civil...)</li>
            </ul>

            <h3 className="text-2xl font-bold mt-10 mb-4">Test pratique : analyse d'un SPA de 200 pages</h3>

            <div className="bg-muted/50 p-6 rounded-lg my-8">
              <h4 className="font-semibold mb-4">Protocole de test :</h4>
              <p className="mb-4">Nous avons soumis à Gemini 2.5 Pro un Share Purchase Agreement (SPA) de 200 pages avec annexes, incluant garanties d'actif et de passif, clauses de earn-out, et mécanismes d'ajustement de prix.</p>
              <p className="text-sm text-muted-foreground">
                <strong>Résultats :</strong> Identification correcte de 23 clauses problématiques sur 25 identifiées par nos experts. Temps d'analyse : 45 secondes. Synthèse structurée avec références précises aux pages concernées.
              </p>
            </div>

            {/* Section 2 */}
            <h2 className="text-3xl font-bold mt-12 mb-6">Multimodalité : au-delà du texte</h2>

            <p className="mb-6">
              <strong>Gemini 2.5</strong> ne se contente pas de lire du texte. Il peut analyser :
            </p>

            <div className="grid md:grid-cols-2 gap-4 my-8">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-start space-x-3">
                    <FileText className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold">Documents scannés</h4>
                      <p className="text-sm text-muted-foreground">OCR intégré de haute qualité, même sur les documents anciens ou de mauvaise qualité.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-start space-x-3">
                    <Globe className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold">Tableaux et graphiques</h4>
                      <p className="text-sm text-muted-foreground">Extraction et interprétation des données visuelles (bilans, organigrammes).</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-start space-x-3">
                    <Code className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold">Documents manuscrits</h4>
                      <p className="text-sm text-muted-foreground">Reconnaissance des annotations manuscrites sur les contrats.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-start space-x-3">
                    <Brain className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold">Images et captures</h4>
                      <p className="text-sm text-muted-foreground">Analyse de captures d'écran, logos, marques pour le droit de la propriété intellectuelle.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Section 3 */}
            <h2 className="text-3xl font-bold mt-12 mb-6">Performances juridiques : notre évaluation</h2>

            <h3 className="text-2xl font-bold mt-10 mb-4">Points forts constatés</h3>

            <ul className="list-disc pl-6 space-y-3 mb-8">
              <li><strong>Synthèse de documents complexes</strong> : excellent pour extraire les points clés de documents volumineux</li>
              <li><strong>Comparaison de versions</strong> : identification précise des différences entre plusieurs versions d'un contrat</li>
              <li><strong>Analyse transversale</strong> : capacité à croiser informations entre plusieurs documents chargés simultanément</li>
              <li><strong>Rapidité</strong> : temps de réponse remarquables même sur des requêtes complexes</li>
            </ul>

            <h3 className="text-2xl font-bold mt-10 mb-4">Limitations observées</h3>

            <ul className="list-disc pl-6 space-y-3 mb-8">
              <li><strong>Pas de sources juridiques intégrées</strong> : contrairement à Lexis AI+ ou Harvey, pas d'accès à des bases de jurisprudence</li>
              <li><strong>Hallucinations possibles</strong> : sur les questions de droit positif, vérification systématique nécessaire</li>
              <li><strong>Raisonnement juridique perfectible</strong> : excellent pour l'analyse, moins fiable pour l'interprétation juridique fine</li>
            </ul>

            {/* Comparison Table */}
            <h2 className="text-3xl font-bold mt-12 mb-6">Gemini 2.5 vs. autres LLMs : comparatif</h2>

            <div className="overflow-x-auto my-8">
              <table className="w-full border-collapse border border-border">
                <thead>
                  <tr className="bg-muted">
                    <th className="border border-border p-3 text-left">Critère</th>
                    <th className="border border-border p-3 text-center">Gemini 2.5 Pro</th>
                    <th className="border border-border p-3 text-center">GPT-4 Turbo</th>
                    <th className="border border-border p-3 text-center">Claude 3 Opus</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-border p-3 font-medium">Fenêtre de contexte</td>
                    <td className="border border-border p-3 text-center text-green-600 font-bold">1M tokens</td>
                    <td className="border border-border p-3 text-center">128K tokens</td>
                    <td className="border border-border p-3 text-center">200K tokens</td>
                  </tr>
                  <tr className="bg-muted/30">
                    <td className="border border-border p-3 font-medium">Multimodalité</td>
                    <td className="border border-border p-3 text-center">⭐⭐⭐⭐⭐</td>
                    <td className="border border-border p-3 text-center">⭐⭐⭐⭐</td>
                    <td className="border border-border p-3 text-center">⭐⭐⭐⭐</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3 font-medium">Rapidité</td>
                    <td className="border border-border p-3 text-center">⭐⭐⭐⭐⭐</td>
                    <td className="border border-border p-3 text-center">⭐⭐⭐</td>
                    <td className="border border-border p-3 text-center">⭐⭐⭐</td>
                  </tr>
                  <tr className="bg-muted/30">
                    <td className="border border-border p-3 font-medium">Raisonnement juridique</td>
                    <td className="border border-border p-3 text-center">⭐⭐⭐⭐</td>
                    <td className="border border-border p-3 text-center">⭐⭐⭐⭐</td>
                    <td className="border border-border p-3 text-center">⭐⭐⭐⭐⭐</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3 font-medium">Prix</td>
                    <td className="border border-border p-3 text-center">$$</td>
                    <td className="border border-border p-3 text-center">$$$</td>
                    <td className="border border-border p-3 text-center">$$$$</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Pros & Cons */}
            <h2 className="text-3xl font-bold mt-12 mb-6">Synthèse : avantages et inconvénients</h2>

            <div className="grid md:grid-cols-2 gap-6 my-8">
              <div>
                <h3 className="text-xl font-bold mb-4 text-green-700">✅ Points forts</h3>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                    <span><strong>Contexte massif</strong> : analyse de documents de plusieurs centaines de pages sans perte d'information</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                    <span><strong>Multimodalité native</strong> : traitement unifié de texte, images, PDF scannés</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                    <span><strong>Rapidité exceptionnelle</strong> : temps de réponse très courts même sur des requêtes complexes</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                    <span><strong>Tarification compétitive</strong> : rapport qualité/prix excellent pour les gros volumes</span>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-4 text-red-700">❌ Limitations</h3>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <XCircle className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                    <span><strong>Modèle généraliste</strong> : pas spécialisé juridique, pas de bases de données intégrées</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <XCircle className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                    <span><strong>Hallucinations possibles</strong> : sur le droit positif, toujours vérifier les affirmations</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <XCircle className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                    <span><strong>Date de coupure</strong> : pas d'accès aux développements juridiques les plus récents</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <XCircle className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                    <span><strong>Confidentialité</strong> : données transitent par les serveurs Google (attention RGPD)</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Use Cases */}
            <h2 className="text-3xl font-bold mt-12 mb-6">Cas d'usage recommandés</h2>

            <div className="bg-muted/50 p-6 rounded-lg my-8">
              <h4 className="font-semibold mb-4">✅ Gemini 2.5 excelle pour :</h4>
              <ul className="space-y-2 mb-6">
                <li>• <strong>Due diligence</strong> : analyse de data rooms volumineuses</li>
                <li>• <strong>Comparaison de contrats</strong> : identifier les différences entre versions</li>
                <li>• <strong>Synthèse de dossiers</strong> : résumer des centaines de pages en quelques points clés</li>
                <li>• <strong>Extraction de données</strong> : tableaux, clauses spécifiques, montants</li>
              </ul>
              
              <h4 className="font-semibold mb-4">⚠️ À compléter avec d'autres outils pour :</h4>
              <ul className="space-y-2">
                <li>• <strong>Recherche juridique</strong> : préférer Lexis AI+ ou Harvey</li>
                <li>• <strong>Conseil juridique</strong> : vérification humaine indispensable</li>
                <li>• <strong>Rédaction contractuelle</strong> : combiner avec des outils spécialisés</li>
              </ul>
            </div>

            {/* Verdict */}
            <h2 className="text-3xl font-bold mt-12 mb-6">Notre verdict final</h2>

            <div className="flex items-center space-x-2 my-6">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} className={`w-8 h-8 ${i <= 4 ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
              ))}
              <span className="ml-4 text-2xl font-bold">4/5</span>
            </div>

            <p className="mb-6">
              <strong>Gemini 2.5 Pro</strong> est un outil remarquable pour l'<strong>analyse documentaire à grande échelle</strong>. Sa fenêtre de contexte record et sa multimodalité native en font un allié précieux pour les opérations de M&A, les due diligences et l'analyse de dossiers complexes.
            </p>

            <p className="mb-6">
              En revanche, ce n'est <strong>pas un outil juridique spécialisé</strong>. Il doit être combiné avec des solutions comme Harvey ou Lexis AI+ pour les tâches nécessitant une expertise juridique intégrée.
            </p>

            <div className="bg-primary/10 border-l-4 border-primary p-6 my-8">
              <div className="flex items-start">
                <Brain className="w-6 h-6 text-primary mt-1 mr-4 flex-shrink-0" />
                <div>
                  <h4 className="text-primary font-semibold text-lg mb-2">Recommandation Gaius</h4>
                  <p className="text-foreground/80 mb-0">
                    Gemini 2.5 Pro est <strong>idéal comme couche d'analyse documentaire</strong> dans un stack juridique complet. Utilisez-le pour ingérer et synthétiser les gros volumes, puis affinez avec des outils spécialisés pour le conseil juridique.
                  </p>
                </div>
              </div>
            </div>

          </div>
          
          {/* CTA */}
          <div className="mt-12 pt-8 border-t">
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild>
                <Link to="/training">
                  Se former à l'IA juridique <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/blog">
                  Lire nos autres tests
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // English version
  return (
    <div className="min-h-screen bg-background">
      <SEOHead 
        title="Gemini 2.5 for Lawyers: Complete Review" 
        description="Google Gemini 2.5 Pro review for legal professionals. 1M token context, multimodality, legal performance." 
        keywords="gemini legal, google gemini lawyer, generative ai lawyers, gemini 2.5 pro, document analysis ai" 
        canonicalUrl="https://gaius-tech.com/articles/test-gemini" 
        alternateUrlFr="https://gaius-tech.com/articles/test-gemini"
        alternateUrlEn="https://gaius-tech.com/articles/test-gemini"
        pageType="article"
        structuredData={structuredData}
      />
      
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-12 text-center">
          <Badge variant="secondary" className="mb-4 bg-blue-100 text-blue-800">AI News</Badge>
          <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            We Have Tested Gemini 2.5 for You
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            Google's new model put to the legal test: 1 million token context, native multimodality and advanced reasoning.
          </p>
          <div className="flex items-center justify-center space-x-6 text-sm text-muted-foreground">
            <span className="flex items-center space-x-1">
              <Calendar className="w-4 h-4" />
              <span>January 20, 2026</span>
            </span>
            <span className="flex items-center space-x-1">
              <Clock className="w-4 h-4" />
              <span>14 min read</span>
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="prose prose-lg max-w-none">
          
          <p className="text-xl leading-relaxed mb-8">
            <strong>Google Gemini 2.5 Pro</strong> represents the culmination of several years of research at Google DeepMind. With its <strong>one million token</strong> context window and native multimodal capabilities, it promises to revolutionize document analysis. We tested it intensively in a demanding legal context.
          </p>

          <p className="mb-8">
            Central question of our test: <strong>Can Gemini 2.5 become the versatile assistant for lawyers?</strong> Here's our detailed analysis.
          </p>

          {/* Key Stats */}
          <div className="bg-primary/10 border-l-4 border-primary p-6 my-8">
            <div className="flex items-start">
              <TrendingUp className="w-6 h-6 text-primary mt-1 mr-4 flex-shrink-0" />
              <div>
                <h4 className="text-primary font-semibold text-lg mb-2">Technical Specifications</h4>
                <ul className="text-foreground/80 space-y-1 mb-0">
                  <li>• <strong>1 million tokens</strong> context window (≈ 750,000 words)</li>
                  <li>• <strong>Native multimodal</strong>: text, images, PDF, audio, video</li>
                  <li>• <strong>Average latency</strong>: 2-4 seconds for complex queries</li>
                  <li>• <strong>Knowledge cutoff</strong>: data up to end of 2025</li>
                </ul>
              </div>
            </div>
          </div>

          <h2 className="text-3xl font-bold mt-12 mb-6">The Context Revolution: Analyzing Hundreds of Pages</h2>

          <p className="mb-6">
            The most impressive feature of <strong>Gemini 2.5 Pro</strong> is its ability to ingest and analyze large documents in one go. Where GPT-4 is limited to about 128,000 tokens, Gemini accepts <strong>eight times more</strong>.
          </p>

          <p className="mb-6">
            In practice, this means being able to load:
          </p>

          <ul className="list-disc pl-6 space-y-3 mb-8">
            <li><strong>A complete M&A contract</strong> with all its annexes</li>
            <li><strong>A litigation file</strong> including briefs, exhibits and case law</li>
            <li><strong>Several hundred pages of due diligence</strong> in a single session</li>
            <li><strong>An entire code</strong> for regulatory analysis</li>
          </ul>

          <h2 className="text-3xl font-bold mt-12 mb-6">Multimodality: Beyond Text</h2>

          <p className="mb-6">
            <strong>Gemini 2.5</strong> doesn't just read text. It can analyze scanned documents, charts and tables, handwritten annotations, and images for intellectual property analysis.
          </p>

          {/* Verdict */}
          <h2 className="text-3xl font-bold mt-12 mb-6">Our Final Verdict</h2>

          <div className="flex items-center space-x-2 my-6">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star key={i} className={`w-8 h-8 ${i <= 4 ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
            ))}
            <span className="ml-4 text-2xl font-bold">4/5</span>
          </div>

          <p className="mb-6">
            <strong>Gemini 2.5 Pro</strong> is a remarkable tool for <strong>large-scale document analysis</strong>. Its record context window and native multimodality make it a valuable ally for M&A operations, due diligences and complex case analysis.
          </p>

          <p className="mb-6">
            However, it's <strong>not a specialized legal tool</strong>. It should be combined with solutions like Harvey or Lexis AI+ for tasks requiring integrated legal expertise.
          </p>

        </div>
        
        {/* CTA */}
        <div className="mt-12 pt-8 border-t">
          <div className="flex flex-col sm:flex-row gap-4">
            <Button asChild>
              <Link to="/training">
                Get trained in Legal AI <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/blog">
                Read our other tests
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
